// <purpose>Presentational squarified treemap, hybrid shape-adaptive tiles. Width/height in → tiles out.</purpose>
// <dependencies>squarify util; colors passed by caller (mode-agnostic).</dependencies>
// <constraints>Pure: width is a prop. Wide/short tile → baseline (abbr ◄─► count); tall/square → stack (abbr·label·count).
// abbr+count always shown; label only when there's room. Fill is always present (height 0 when none) so it RISES on flip (Morph+).</constraints>
import React from 'react';
import { theme } from '../theme';
import { squarify } from '../services/squarify';

export interface TreemapTile {
  key: string;
  abbr: string;         // always shown (K0 / VW …)
  label: string;        // shown only when there's room
  value: number;        // area weight
  displayValue: number; // number shown
  ariaLabel: string;
  fillRate?: number;    // 0..1 — Füllstand height
}

interface TreemapProps {
  tiles: TreemapTile[];
  width: number;
  height: number;
  tileColor: string;
  textColor: string;
  fillColor?: string;
}

const GAP = 3;
const MIN_AREA_FRACTION = 0.05; // min-tile floor
// A tile shorter than this can't host the stacked Zahl-Held (abbr · label · count) at the hero
// font sizes, so it falls back to the single-line baseline row. Aligned to the h≥60 count tier.
const ROW_MAX_H = 60;

// <constraints>Layout is HEIGHT-gated, not aspect-driven: row mode is only for a genuine short/wide
// strip — a tall tile always stacks (Zahl-Held), however wide. The wide/tall split is square (w > h,
// i.e. 50/50). Within a stack, label is shown when there's width (CSS-ellipsis truncates), else dropped.
// Fonts step by tile height. Pure fn so the rule is unit-testable independent of squarify.</constraints>
export function tileGeometry(w: number, h: number): {
  isRow: boolean; countFs: number; abbrFs: number; labelFs: number; showLabel: boolean;
} {
  return {
    isRow: h < ROW_MAX_H && w > h,                  // short AND wider-than-square → baseline row
    countFs: h >= 60 ? 23 : h >= 36 ? 17 : 14,      // size tiers, +1 over the original 22/16/13
    abbrFs: h >= 60 ? 12 : h >= 36 ? 11 : 10,        // +1 over 11/10/9
    labelFs: 11,                                     // +1 over the original flat 10
    showLabel: w >= 84 && h >= 38,
  };
}

export const Treemap: React.FC<TreemapProps> = ({ tiles, width, height, tileColor, textColor, fillColor }) => {
  const rects = squarify(tiles.map(t => ({ key: t.key, value: t.value })), width, height, MIN_AREA_FRACTION);
  const byKey = new Map(tiles.map(t => [t.key, t]));
  // Ripple order: big tiles first (transition-delay by area rank). prefers-reduced-motion zeroes it globally (index.css).
  const delayByKey = new Map([...rects].sort((a, b) => b.w * b.h - a.w * a.h).map((r, i) => [r.key, i * 0.05]));

  return (
    <div style={{ position: 'relative', width, height }}>
      {rects.map(r => {
        const t = byKey.get(r.key)!;
        const w = Math.max(0, r.w - GAP);
        const h = Math.max(0, r.h - GAP);
        const { isRow, countFs, abbrFs, labelFs, showLabel } = tileGeometry(w, h);
        const fillH = fillColor && t.fillRate ? `${Math.min(100, Math.round(t.fillRate * 100))}%` : '0%';
        // a11y: full m.tileText (no opacity blend) — the 0.55/0.62 blends dropped abbr/label under WCAG-AA 4.5:1
        // (esp. rejection palette: 2.23/2.50:1). Hierarchy stays via size + weight (count is bigger + 800). [2026-06-22]
        const abbrEl = <span style={{ position: 'relative', zIndex: 1, fontSize: abbrFs, fontWeight: 700, whiteSpace: 'nowrap', lineHeight: 1.05 }}>{t.abbr}</span>;
        const countEl = <span style={{ position: 'relative', zIndex: 1, fontSize: countFs, fontWeight: 800, lineHeight: 1 }}>{t.displayValue}</span>;
        const labelEl = showLabel ? <span style={{ position: 'relative', zIndex: 1, fontSize: labelFs, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.12 }}>{t.label}</span> : null;
        return (
          <div key={r.key} role="group" aria-label={t.ariaLabel} title={t.label} tabIndex={0}
            style={{
              position: 'absolute', left: r.x, top: r.y, width: w, height: h,
              background: tileColor, color: textColor, borderRadius: 4, overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              justifyContent: isRow ? 'flex-end' : 'flex-start',
              padding: '4px 6px', boxSizing: 'border-box', fontFamily: theme.font.family,
            }}>
            <div data-stx-fill aria-hidden="true" style={{
              position: 'absolute', left: 0, right: 0, bottom: 0, height: fillH,
              background: fillColor || 'transparent',
              transition: 'height 0.72s cubic-bezier(0.34,1.55,0.5,1)', transitionDelay: `${delayByKey.get(r.key) || 0}s`,
            }} />
            {isRow ? (
              <>
                {labelEl && <div style={{ position: 'relative', zIndex: 1, marginBottom: 'auto' }}>{labelEl}</div>}
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 5 }}>{abbrEl}{countEl}</div>
              </>
            ) : (
              <>
                {abbrEl}
                {labelEl}
                <span style={{ marginTop: 'auto' }}>{countEl}</span>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
